import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router';
import type { Player, PlayerStats, GameSession } from '@beat-games/shared-types';
import { WalletAvatar } from '../components/ui/WalletAvatar';

const API_BASE = import.meta.env.VITE_API_URL || '';

function getPhaseColor(phase: string): string {
  switch (phase) {
    case 'QUALIFIER': return '#22c55e';
    case 'SHORTLIST': return '#3b82f6';
    case 'SEMIFINAL': return '#a855f7';
    case 'FINAL': return '#fbbf24';
    default: return '#22c55e';
  }
}

function getPhaseLabel(phase: string): string {
  const labels: Record<string, string> = {
    QUALIFIER: 'Qualifier',
    SHORTLIST: 'Shortlist',
    SEMIFINAL: 'Semifinal',
    FINAL: 'Final',
    CLOSED: 'Closed',
  };
  return labels[phase] || phase;
}

export default function Profile() {
  const { wallet } = useParams<{ wallet: string }>();
  const [player, setPlayer] = useState<Player | null>(null);
  const [stats, setStats] = useState<PlayerStats | null>(null);
  const [history, setHistory] = useState<GameSession[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchProfile() {
      if (!wallet) return;

      try {
        const response = await fetch(`${API_BASE}/api/player/${wallet}`);
        const data = await response.json();
        if (data.success) {
          setPlayer(data.data.player);
          setStats(data.data.stats);
          setHistory(data.data.history);
        } else {
          setError(data.error?.message || 'Player not found');
        }
      } catch {
        setError('Failed to load profile');
      } finally {
        setIsLoading(false);
      }
    }

    fetchProfile();
  }, [wallet]);

  const formatWallet = (address: string) =>
    `${address.slice(0, 4)}...${address.slice(-4)}`;

  // Format survival time as seconds
  const formatTime = (ms: number | null | undefined) =>
    ms !== null && ms !== undefined ? (ms / 1000).toFixed(3) + 's' : '-';

  // Format score as points
  const formatPoints = (score: number | null) =>
    score !== null ? score.toLocaleString() + ' pts' : '-';

  // Format precision penalty (negative value)
  const formatPrecisionPenalty = (precisionError: number | undefined) => {
    if (precisionError === undefined) return '';
    const penalty = Math.floor(precisionError * 0.8);
    return penalty > 0 ? `-${penalty}` : '0';
  };

  const formatDate = (dateString: string) => {
    // SQLite stores UTC without 'Z' suffix, so append it for correct parsing
    const utcString = dateString.endsWith('Z') ? dateString : dateString.replace(' ', 'T') + 'Z';
    const date = new Date(utcString);
    return date.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#030303] text-white flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-2 border-white/10 border-t-white/50 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-white/40 font-mono text-sm">Loading profile...</p>
        </div>
      </div>
    );
  }

  if (error || !player) {
    return (
      <div className="min-h-screen bg-[#030303] text-white">
        <div className="grain-overlay" />
        <div className="relative z-10 flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 border border-[#ef4444]/20 bg-[#ef4444]/5 rounded-full flex items-center justify-center">
              <svg className="w-8 h-8 text-[#ef4444]/60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
            <h2 className="font-display text-xl mb-2">Player Not Found</h2>
            <p className="text-white/40 mb-6">{error || 'This player does not exist'}</p>
            <Link
              to="/leaderboard"
              className="inline-flex items-center gap-2 px-6 py-3 bg-white text-black font-bold text-sm tracking-wider"
            >
              <span>VIEW LEADERBOARD</span>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#030303] text-white overflow-x-hidden">
      {/* Grain overlay */}
      <div className="grain-overlay" />

      {/* Background */}
      <div className="fixed inset-0 pointer-events-none">
        <div
          className="absolute inset-0 opacity-[0.015]"
          style={{
            backgroundImage: `
              linear-gradient(to right, white 1px, transparent 1px),
              linear-gradient(to bottom, white 1px, transparent 1px)
            `,
            backgroundSize: '50px 50px',
          }}
        />
        <div
          className="absolute inset-0"
          style={{
            background: 'radial-gradient(ellipse 80% 50% at 50% 0%, rgba(255,255,255,0.02) 0%, transparent 60%)',
          }}
        />
      </div>

      <div className="relative z-10">
        {/* Header */}
        <header className="pt-12 pb-8 px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center justify-center gap-4 mb-6 animate-slide-up-fade">
              <div className="h-px flex-1 max-w-[100px] bg-gradient-to-r from-transparent to-white/20" />
              <span className="font-mono text-[10px] tracking-[0.4em] text-white/30">PLAYER PROFILE</span>
              <div className="h-px flex-1 max-w-[100px] bg-gradient-to-l from-transparent to-white/20" />
            </div>

            <div className="text-center animate-slide-up-fade" style={{ animationDelay: '0.05s' }}>
              <div className="flex justify-center mb-4">
                <WalletAvatar
                  address={player.walletAddress}
                  size={80}
                  className="border-2 border-white/10"
                />
              </div>
              <h1 className="font-mono text-2xl sm:text-3xl font-bold text-white mb-2">
                {formatWallet(player.walletAddress)}
              </h1>
              <button
                onClick={() => navigator.clipboard.writeText(player.walletAddress)}
                className="inline-flex items-center gap-2 text-white/40 hover:text-white/70 transition-colors text-sm"
              >
                <span className="font-mono text-xs">{player.walletAddress}</span>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              </button>
            </div>

            {/* Badges */}
            {player.badges.length > 0 && (
              <div className="flex justify-center gap-2 mt-4 animate-slide-up-fade" style={{ animationDelay: '0.1s' }}>
                {player.badges.map((badge) => (
                  <span
                    key={badge}
                    className="px-3 py-1 bg-white/5 border border-white/10 rounded-full text-sm text-white/70"
                  >
                    {badge}
                  </span>
                ))}
              </div>
            )}
          </div>
        </header>

        {/* Stats Grid */}
        <div className="max-w-4xl mx-auto px-4 mb-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 animate-slide-up-fade" style={{ animationDelay: '0.15s' }}>
            <div className="bg-white/[0.02] border border-white/[0.06] p-4">
              <div className="font-mono text-[10px] text-white/30 tracking-wider mb-1">TOTAL GAMES</div>
              <div className="font-display text-2xl font-bold text-white">
                {stats?.totalGames ?? 0}
              </div>
            </div>
            <div className="bg-white/[0.02] border border-white/[0.06] p-4">
              <div className="font-mono text-[10px] text-white/30 tracking-wider mb-1">BEST SCORE</div>
              <div className="font-mono text-2xl font-bold text-[#22c55e]">
                {formatPoints(stats?.bestScore ?? null)}
              </div>
            </div>
            <div className="bg-white/[0.02] border border-white/[0.06] p-4">
              <div className="font-mono text-[10px] text-white/30 tracking-wider mb-1">AVERAGE</div>
              <div className="font-mono text-2xl font-bold text-white/70">
                {formatPoints(stats?.averageScore ? Math.round(stats.averageScore) : null)}
              </div>
            </div>
            <div className="bg-white/[0.02] border border-white/[0.06] p-4">
              <div className="font-mono text-[10px] text-white/30 tracking-wider mb-1">
                {stats?.currentPhaseStatus?.phaseType ? getPhaseLabel(stats.currentPhaseStatus.phaseType).toUpperCase() : 'CURRENT'} RANK
              </div>
              <div className="font-display text-2xl font-bold">
                {stats?.currentPhaseStatus?.eliminationRank ? (
                  <span className="text-[#ef4444]">
                    Eliminated #{stats.currentPhaseStatus.eliminationRank}
                  </span>
                ) : stats?.currentPhaseStatus?.rank ? (
                  <span className="text-[#22c55e]">
                    #{stats.currentPhaseStatus.rank}
                  </span>
                ) : stats?.currentPhaseStatus?.isQualified ? (
                  <span className="text-white/50">No games</span>
                ) : (
                  <span className="text-white/30">Not qualified</span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Phase History */}
        {stats?.phaseHistory && stats.phaseHistory.length > 0 && (
          <div className="max-w-4xl mx-auto px-4 mb-8">
            <div className="flex items-center gap-3 mb-4 animate-slide-up-fade" style={{ animationDelay: '0.18s' }}>
              <span className="font-mono text-[10px] text-white/30 tracking-[0.2em]">PHASE HISTORY</span>
              <div className="flex-1 h-px bg-white/10" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 animate-slide-up-fade" style={{ animationDelay: '0.2s' }}>
              {stats.phaseHistory.map((phase) => {
                const phaseColor = getPhaseColor(phase.phaseType);
                return (
                  <div
                    key={phase.phaseType}
                    className="bg-white/[0.02] border border-white/[0.06] p-4"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span
                        className="px-2 py-0.5 font-mono text-[10px] tracking-wider"
                        style={{
                          backgroundColor: `${phaseColor}15`,
                          color: phaseColor,
                        }}
                      >
                        {getPhaseLabel(phase.phaseType)}
                      </span>
                      {phase.status === 'qualified' && (
                        <span className="inline-flex items-center gap-1 text-[#22c55e] font-mono text-[9px] tracking-wider">
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          ADVANCED
                        </span>
                      )}
                      {phase.status === 'eliminated' && (
                        <span className="inline-flex items-center gap-1 text-[#ef4444] font-mono text-[9px] tracking-wider">
                          OUT #{phase.eliminationRank}
                        </span>
                      )}
                      {phase.status === 'active' && (
                        <span className="inline-flex items-center gap-1.5 text-[#22c55e] font-mono text-[9px] tracking-wider">
                          <span className="relative flex h-2 w-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#22c55e] opacity-75" />
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-[#22c55e]" />
                          </span>
                          ACTIVE
                        </span>
                      )}
                    </div>
                    <div className="font-mono text-lg text-white mb-1">
                      {formatPoints(phase.bestScore)}
                    </div>
                    <div className="font-mono text-[10px] text-white/30">
                      {phase.gamesPlayed} game{phase.gamesPlayed !== 1 ? 's' : ''}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Game History */}
        <div className="max-w-4xl mx-auto px-4 pb-12">
          <div className="flex items-center gap-3 mb-4 animate-slide-up-fade" style={{ animationDelay: '0.2s' }}>
            <span className="font-mono text-[10px] text-white/30 tracking-[0.2em]">RECENT GAMES</span>
            <div className="flex-1 h-px bg-white/10" />
            <span className="font-mono text-[10px] text-white/20">{history.length} games</span>
          </div>

          {history.length === 0 ? (
            <div className="text-center py-16 border border-white/[0.06] bg-white/[0.01] animate-slide-up-fade" style={{ animationDelay: '0.25s' }}>
              <div className="w-16 h-16 mx-auto mb-4 border border-white/10 bg-white/[0.02] rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-white/20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="font-display text-xl mb-2">No Games Yet</h3>
              <p className="text-white/40 text-sm mb-6">This player hasn't completed any games</p>
              <Link
                to="/play"
                className="group inline-flex items-center gap-3 px-6 py-3 bg-white text-black font-bold text-sm tracking-wider transition-all hover:shadow-[0_0_30px_rgba(255,255,255,0.3)]"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M8 5.14v14.72a1 1 0 001.5.86l11-7.36a1 1 0 000-1.72l-11-7.36a1 1 0 00-1.5.86z" />
                </svg>
                <span>PLAY NOW</span>
                <svg className="w-4 h-4 transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </Link>
            </div>
          ) : (
            <div className="border border-white/[0.06] bg-white/[0.01] overflow-hidden animate-slide-up-fade" style={{ animationDelay: '0.25s' }}>
              <div className="divide-y divide-white/[0.06]">
                {history.map((session) => {
                  const phaseColor = session.phaseType ? getPhaseColor(session.phaseType) : '#22c55e';
                  const isPerfect = session.score !== null && session.score >= 45000;

                  return (
                    <div
                      key={session.id}
                      className="flex items-center gap-4 px-4 py-3 hover:bg-white/[0.02] transition-colors"
                    >
                      {/* Phase badge */}
                      <div className="w-20 flex-shrink-0">
                        {session.phaseType ? (
                          <span
                            className="inline-block px-2 py-0.5 font-mono text-[10px] tracking-wider"
                            style={{
                              backgroundColor: `${phaseColor}15`,
                              color: `${phaseColor}`,
                            }}
                          >
                            {getPhaseLabel(session.phaseType)}
                          </span>
                        ) : (
                          <span className="text-white/20 font-mono text-[10px]">-</span>
                        )}
                      </div>

                      {/* Score */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className={`font-mono text-lg ${session.status === 'completed' ? 'text-white' : 'text-white/40'}`}>
                            {session.score !== null ? session.score.toLocaleString() : '-'} <span className="text-white/30 text-xs">pts</span>
                          </span>
                          {isPerfect && (
                            <span className="px-1.5 py-0.5 bg-[#fbbf24]/10 text-[#fbbf24] font-mono text-[9px] tracking-wider">
                              PERFECT
                            </span>
                          )}
                          {session.status === 'invalidated' && (
                            <span className="px-1.5 py-0.5 bg-[#ef4444]/10 text-[#ef4444] font-mono text-[9px] tracking-wider">
                              INVALID
                            </span>
                          )}
                        </div>
                        {session.survivalTimeMs !== undefined && session.precisionError !== undefined && (
                          <div className="font-mono text-[10px] text-white/30">
                            {formatTime(session.survivalTimeMs)} <span className="text-[#ef4444]/60">{formatPrecisionPenalty(session.precisionError)}</span>
                          </div>
                        )}
                      </div>

                      {/* Date */}
                      <div className="text-white/30 font-mono text-xs hidden sm:block">
                        {formatDate(session.startedAt)}
                      </div>

                      {/* Replay link */}
                      <div className="flex justify-end">
                        {session.hasReplay && session.status === 'completed' ? (
                          <Link
                            to={`/replay/${session.id}`}
                            className="inline-flex items-center gap-1.5 text-white/40 hover:text-white transition-colors"
                          >
                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M8 5.14v14.72a1 1 0 001.5.86l11-7.36a1 1 0 000-1.72l-11-7.36a1 1 0 00-1.5.86z" />
                            </svg>
                            <span className="font-mono text-[10px] tracking-wider">WATCH</span>
                          </Link>
                        ) : (
                          <span className="w-16" />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* CTA */}
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/play"
              className="group inline-flex items-center gap-3 px-6 py-3 bg-white text-black font-bold text-sm tracking-wider transition-all hover:shadow-[0_0_30px_rgba(255,255,255,0.3)]"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M8 5.14v14.72a1 1 0 001.5.86l11-7.36a1 1 0 000-1.72l-11-7.36a1 1 0 00-1.5.86z" />
              </svg>
              <span>PLAY NOW</span>
              <svg className="w-4 h-4 transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/leaderboard"
              className="inline-flex items-center gap-2 px-6 py-3 border border-white/10 text-white/50 hover:text-white/80 hover:border-white/20 transition-all font-mono text-sm tracking-wider"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              <span>LEADERBOARD</span>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
